import React, { useEffect, useState } from 'react';
import { Form, Input, Picker, Button, List, CalendarPicker, Space } from 'antd-mobile';
import dayjs from 'dayjs';
import axiosInstance from "../axiosInstance";

const dateFormat = 'YYYY-MM-DD';

const InspectionForm = ({ inspection, onSubmit, onCancel, onDelete }) => {
  const [form] = Form.useForm();
  const [visibleCalendar, setVisibleCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [visibleProjectPicker, setVisibleProjectPicker] = useState(false);
  const [visiblePMPicker, setVisiblePMPicker] = useState(false);
  const [visiblePeriodPicker, setVisiblePeriodPicker] = useState(false);
  const [visibleInspection, setVisibleInspection] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedPM, setSelectedPM] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedInspectionType, setSelectedInspectionType] = useState('');
  const [projects, setProjects] = useState([]);
  const [pms, setPMs] = useState([]);
  const [selectedPMInfo, setSelectedPMInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsResponse, pmsResponse] = await Promise.all([
          axiosInstance.get('/api/projects'),
          axiosInstance.get('/api/pms')
        ]);
        setProjects(projectsResponse.data);
        setPMs(pmsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (inspection && projects.length > 0 && pms.length > 0) {
      const pm = pms.find((p) => p.name === inspection.pm_name);

      form.setFieldsValue({
        bco_number: inspection.bco_number,
        inspection_type: inspection.inspection_type,
        pm_name: inspection.pm_name,
        pm_contact_number: inspection.pm_contact_number,
        pm_contact_email: inspection.pm_contact_email
      });

      setSelectedDate(inspection.inspection_date ? dayjs(inspection.inspection_date).format(dateFormat) : null);
      setSelectedProject(projects.find(p => p.name === inspection.project_name)?.id || '');
      setSelectedPM(pm?.id || '');
      setSelectedPeriod(inspection.time_period || '');
      setSelectedInspectionType(inspection.inspection_type || '');

      if (pm) {
        setSelectedPMInfo({
          name: pm.name,
          contact_number: pm.contact_number,
          contact_email: pm.contact_email
        });
      }
    }
  }, [inspection, projects, pms, form]);

  const inspectionTypes = [
    { value: 'IFO', label: 'IFO - Foundation' },
    { value: 'ICB', label: 'ICB - Concrete block' },
    { value: 'ITK', label: 'ITK - Waterproofing Membrane' },
    { value: 'IPP', label: 'IPP - Plumbing' },
    { value: 'ISF', label: 'ISF - Floor slab' },
    { value: 'IFG', label: 'IFG - Framing' },
    { value: 'ICA', label: 'ICA - Cavity Wrap' },
    { value: 'ICL', label: 'ICL - Cladding' },
    { value: 'IPB', label: 'IPB - Building preline' },
    { value: 'IPL', label: 'IPL - Postline' },
    { value: 'IDT', label: 'IDT - Drainage' },
    { value: 'IF1', label: 'IF1 - Residential Final' },
    { value: 'IF2', label: 'IF2 - Commercial final' },
    { value: 'IME', label: 'IME - Site Meeting' },
    { value: 'CPU', label: 'CPU - Certificate of public use' },
    { value: 'PCO', label: 'PCO - Pre-construction Commercial' },
  ];

  const timePeriods = [
      { value: 'ALL DAY', label: 'All Day' },
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' },

  ];

  const handleProjectChange = (value) => {
    if (!value || !value[0]) return;

    const projectId = value[0];
    const project = projects.find((p) => p.id === projectId);
    if (project) {
      form.setFieldsValue({
        bco_number: project.bco_number
      });
      setSelectedProject(projectId);
    }
  };

  const handlePMChange = (value) => {
    if (!value || !value[0]) return;
    const pmId = value[0];
    const selectedPM = pms.find(pm => pm.id === pmId);

    if (selectedPM) {
      setSelectedPM(pmId);
      setSelectedPMInfo({
        name: selectedPM.name,
        contact_number: selectedPM.contact_number,
        contact_email: selectedPM.contact_email
      });
    }
  };

  const handlePeriodChange = (value) => {
    if (!value || !value[0]) return;
    setSelectedPeriod(value[0]);
  };

  const handleOk = () => {
    const projectData = projects.find(project => project.id === selectedProject);
    const selectedPMData = pms.find(pm => pm.id === selectedPM);

    if (!projectData || !selectedPMData || !selectedDate || !selectedInspectionType) {
      return;
    }

    const inspectionPayload = {
      project_name: projectData.name,
      bco_number: form.getFieldValue('bco_number'),
      inspection_date: dayjs(selectedDate).format(dateFormat),
      inspection_type: selectedInspectionType,
      pm_name: selectedPMData.name,
      pm_contact_number: selectedPMData.contact_number,
      pm_contact_email: selectedPMData.contact_email,
      time_period: selectedPeriod || null,
      status: inspection?.status || 'Pending'
    };

    if (inspection?.id) {
      inspectionPayload.id = inspection.id;
    }

    onSubmit(inspectionPayload);
  };

  return (
    <div>
      <Form form={form} layout="vertical" footer={
        <Space justify='center' block style={{ '--gap': '15%' }}>
          {inspection && (
            <Button color="danger" onClick={() => onDelete(inspection.id)}>
              Delete
            </Button>
          )}
          <Button color='warning' onClick={onCancel}>Cancel</Button>
          <Button color="primary" onClick={handleOk}>
            {inspection ? 'Update' : 'Submit'}
          </Button>
        </Space>
      }>
        <Form.Item
          label="Project"
          rules={[{required: true, message: 'Please select a project'}]}
        >
          <List.Item onClick={() => setVisibleProjectPicker(true)}>
            {selectedProject ? projects.find(p => p.id === selectedProject)?.name : 'Select a project'}
          </List.Item>
          <Picker
            columns={[projects.map((project) => ({label: project.name, value: project.id}))]}
            visible={visibleProjectPicker}
            onClose={() => setVisibleProjectPicker(false)}
            value={selectedProject ? [selectedProject] : []}
            onConfirm={handleProjectChange}
          />
        </Form.Item>

        <Form.Item
          label="Project Manager"
          rules={[{required: true, message: 'Please select a PM'}]}
        >
          <List.Item onClick={() => setVisiblePMPicker(true)}>
            {selectedPM ? pms.find(p => p.id === selectedPM)?.name : 'Select a PM'}
          </List.Item>
          <Picker
            columns={[pms.map((pm) => ({label: pm.name, value: pm.id}))]}
            visible={visiblePMPicker}
            onClose={() => setVisiblePMPicker(false)}
            value={selectedPM ? [selectedPM] : []}
            onConfirm={handlePMChange}
          />
        </Form.Item>

        {selectedPMInfo && (
          <>
            <Form.Item label="PM Contact Number">
              <div className="form-static-text">{selectedPMInfo.contact_number}</div>
            </Form.Item>
            <Form.Item label="PM Email">
              <div className="form-static-text">{selectedPMInfo.contact_email}</div>
            </Form.Item>
          </>
        )}

        <Form.Item
          label="Inspection Type"
          rules={[{required: true, message: 'Please select an inspection type'}]}
        >
          <List.Item onClick={() => setVisibleInspection(true)}>
            {selectedInspectionType
              ? inspectionTypes.find(type => type.value === selectedInspectionType)?.label
              : 'Select inspection type'}
          </List.Item>
          <Picker
            columns={[inspectionTypes]}
            visible={visibleInspection}
            onClose={() => setVisibleInspection(false)}
            value={selectedInspectionType ? [selectedInspectionType] : []}
            onConfirm={(value) => {
              if (value?.[0]) {
                setSelectedInspectionType(value[0]);
                setVisibleInspection(false);
              }
            }}
          />
        </Form.Item>

        <Form.Item
          label="Inspection Date"
          rules={[{required: true, message: 'Please select inspection date'}]}
        >
          <List.Item onClick={() => setVisibleCalendar(true)}>
            {selectedDate ? dayjs(selectedDate).format(dateFormat) : 'Select a date'}
          </List.Item>
          <CalendarPicker
            visible={visibleCalendar}
            selectionMode="single"
            value={selectedDate ? dayjs(selectedDate).toDate() : null}
            onClose={() => setVisibleCalendar(false)}
            onMaskClick={() => setVisibleCalendar(false)}
            onChange={(value) => {
              setSelectedDate(value);
              setVisibleCalendar(false);
            }}
          />
        </Form.Item>

        <Form.Item
          label="Time Period"
        >
          <List.Item onClick={() => setVisiblePeriodPicker(true)}>
          {selectedPeriod || 'ALL DAY'}  {/* 默认显示 ALL DAY */}
          </List.Item>
          <Picker
          columns={[timePeriods]}
          visible={visiblePeriodPicker}
          onClose={() => setVisiblePeriodPicker(false)}
          value={selectedPeriod ? [selectedPeriod] : ['ALL DAY']}  // 默认选中 ALL DAY
          onConfirm={handlePeriodChange}
        />
        </Form.Item>

        <Form.Item
          name="bco_number"
          label="BCO Number"
          rules={[{required: true, message: 'Please enter BCO number'}]}
        >
          <Input placeholder="Enter BCO number"/>
        </Form.Item>
      </Form>

      <style>
        {`
          .form-static-text {
            padding: 12px;
            background-color: #f5f5f5;
            border-radius: 4px;
            color: #666;
          }
        `}
      </style>
    </div>
  );
};

export default InspectionForm;